import { isFileSystemAccessSupported } from './fileSystemAccess';
import { navigateDirectoryHandleByPath } from './filePathUtils';
import { ROOT_PROJECT_FOLDER_PATH } from './projectDetection';

export interface ProjectLocation {
  projectId: string;
  folderPath: string;
  projectName: string;
}

export interface SetProjectDirectory {
  (
    projectId: string,
    dirHandle: FileSystemDirectoryHandle,
    path?: string
  ): Promise<void>;
}

/**
 * Save project locations for Electron environment
 */
async function saveLocationsElectron(
  projects: ProjectLocation[],
  rootPath: string,
  setProjectDirectory: SetProjectDirectory
): Promise<void> {
  for (const project of projects) {
    try {
      if (project.folderPath === ROOT_PROJECT_FOLDER_PATH) {
        const dirHandle = {
          name: project.projectName,
          kind: 'directory' as const,
        } as FileSystemDirectoryHandle;

        await setProjectDirectory(project.projectId, dirHandle, rootPath);
        continue;
      }

      const pathParts = project.folderPath.split('/');
      // Construct the full path by combining root path with project folder path
      const projectPath = pathParts.reduce(
        (acc, part) => `${acc}/${part}`,
        rootPath
      );

      const dirHandle = {
        name: pathParts[pathParts.length - 1] || project.projectName,
        kind: 'directory' as const,
      } as FileSystemDirectoryHandle;

      await setProjectDirectory(project.projectId, dirHandle, projectPath);
    } catch (error) {
      console.warn(
        `Failed to save location for project ${project.projectName}:`,
        error
      );
    }
  }
}

/**
 * Save project locations for web browsers with File System Access API
 */
async function saveLocationsWeb(
  projects: ProjectLocation[],
  rootDirHandle: FileSystemDirectoryHandle,
  setProjectDirectory: SetProjectDirectory
): Promise<void> {
  for (const project of projects) {
    try {
      if (project.folderPath === ROOT_PROJECT_FOLDER_PATH) {
        await setProjectDirectory(project.projectId, rootDirHandle);
        continue;
      }

      // Extract the project folder name from the folder path
      // folderPath is like "ProjectName" or "ParentFolder/ProjectName"
      const pathParts = project.folderPath.split('/');
      const projectHandle = await navigateDirectoryHandleByPath(
        rootDirHandle,
        pathParts,
        project.projectName
      );

      if (projectHandle) {
        await setProjectDirectory(project.projectId, projectHandle);
      }
    } catch (error) {
      console.warn(
        `Failed to save location for project ${project.projectName}:`,
        error
      );
    }
  }
}

/**
 * Save local locations for multiple projects in a batch
 * Uses the root directory handle/path that was already selected during folder selection
 */
export async function saveProjectLocationsBatch(
  projects: ProjectLocation[],
  isElectron: boolean,
  rootDirHandle: FileSystemDirectoryHandle | null,
  rootPath: string | undefined,
  setProjectDirectory: SetProjectDirectory
): Promise<void> {
  if (projects.length === 0) {
    return;
  }

  // Need either rootDirHandle (for web) or rootPath (for Electron)
  if (!rootDirHandle && !rootPath) {
    console.warn(
      'No root directory handle or path available to save project locations'
    );
    return;
  }

  try {
    // For Electron, use the stored root path (prioritize Electron)
    if (isElectron && rootPath) {
      await saveLocationsElectron(projects, rootPath, setProjectDirectory);
      return;
    }

    // For web browsers with File System Access API
    if (isFileSystemAccessSupported() && rootDirHandle) {
      await saveLocationsWeb(projects, rootDirHandle, setProjectDirectory);
      return;
    }
  } catch (error) {
    // Silently fail - we don't want to interrupt the upload process
    console.warn('Failed to save project locations:', error);
  }
}
